import React, { useCallback, useRef, useState } from 'react';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { PickerRangeValue } from '@mui/x-date-pickers/internals';
import { PartialDateRange } from 'libs/end-to-end/src/date-range';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';
import { NoopTouchTarget } from 'apps/sigma2-ui/src/components/ui/NoopTouchTarget';

export interface S2DateRangePickerProps {
  value?: PartialDateRange;
  onChange?: (range: PartialDateRange) => void;
  onCommit?: (range: PartialDateRange) => void;
  initialValue?: PartialDateRange;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  label?: 'default' | string;
  outline?: boolean;
  disabled?: boolean;
}

export const S2DateRangePicker: React.FC<S2DateRangePickerProps> = ({
  value,
  onChange,
  onCommit,
  initialValue,
  open: openProp,
  onOpenChange,
  label,
  outline,
  disabled,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  const isControlled = value !== undefined;

  const [internalRange, setInternalRange] = useState<
    [Date | null, Date | null]
  >(() => {
    const src = value ?? initialValue;
    return src ? [src.start ?? null, src.end ?? null] : [null, null];
  });

  const toValidOrNull = (d: Date | null | undefined): Date | null =>
    d && !Number.isNaN(d.getTime()) ? d : null;

  const rangeTuple: [Date | null, Date | null] = isControlled
    ? [toValidOrNull(value.start), toValidOrNull(value.end)]
    : internalRange;

  const latestRangeRef = useRef<[Date | null, Date | null]>(rangeTuple);
  latestRangeRef.current = rangeTuple;

  const internalOnChange = useCallback(
    (pickerValue: PickerRangeValue) => {
      const raw: [Date | null, Date | null] = pickerValue ?? [null, null];
      const next: [Date | null, Date | null] = [
        toValidOrNull(raw[0]),
        toValidOrNull(raw[1]),
      ];

      if (!isControlled) {
        setInternalRange(next);
      }

      latestRangeRef.current = next;

      const [start, end] = next;
      onChange?.({ start, end });
    },
    [isControlled, onChange],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    const [start, end] = latestRangeRef.current;
    onCommit?.({ start, end });
  }, [onCommit, setOpen]);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    setOpen(true);
  }, [disabled, setOpen]);

  const language = useLanguage();

  return (
    <div
      className={cn('w-fit', {
        'border-transparent border-b-2': outline,
      })}
    >
      <TouchTarget
        content={() => {
          return (
            <Spacing
              direction="vertical"
              size="none"
              className={cn('self-center', {
                'px-2 pt-2 pb-1.5 rounded border-1 cursor-pointer': outline,
                'bg-white border-gray-200 group-hover:bg-blue-100 hover:bg-blue-100':
                  outline && !disabled,
                'bg-gray-200 border-gray-100': outline && disabled,
              })}
            >
              {label && (
                <S2Typography
                  variant="small-label"
                  textColorVariant="gray-600"
                  translatedContent={
                    label === 'default'
                      ? language.dateRangeDefaultLabel()
                      : label
                  }
                />
              )}

              <NoopTouchTarget>
                <DateRangePicker
                  calendars={1}
                  disableFuture
                  value={rangeTuple}
                  onChange={internalOnChange}
                  open={open}
                  onOpen={handleOpen}
                  onClose={handleClose}
                  disabled={disabled}
                  slots={{ field: MultiInputDateRangeField }}
                  slotProps={{
                    textField: {
                      fullWidth: false,
                      variant: 'standard',
                      label: false,
                      error: false,
                      sx: {
                        '& .MuiPickersInputBase-root': {
                          margin: 0,
                        },
                        '& .MuiPickersSectionList-root': {
                          width: 'fit-content',
                          padding: 0,
                          fontWeight: 'medium',
                          fontSize: '0.88rem',
                        },
                        '& .MuiPickersFilledInput-root:before, & .MuiPickersFilledInput-root::before':
                          {
                            borderBottomColor: ConfigColors.Gray.Primary.raw,
                          },
                        '& .MuiPickersFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before':
                          {
                            borderBottomColor: ConfigColors.Gray.Primary.raw,
                          },
                        '& .MuiPickersFilledInput-root.Mui-focused:after': {
                          borderBottomColor: ConfigColors.Royal.Secondary.raw,
                        },
                      },
                    },
                  }}
                />
              </NoopTouchTarget>
            </Spacing>
          );
        }}
        behavior={{
          type: 'onClick',
          onClick: handleClick,
        }}
      />
    </div>
  );
};
